import * as Sentry from '@sentry/react';
import { EMPTY_STATE_IMAGES } from '@shared/constants';
import { Button } from '@sketchnote/shared-components';
import axios from 'axios';
import { detect } from 'detect-browser';
import { useRouter } from 'next/router';
import { EmptyStateDashboard } from 'src/features/dashboard/components/EmptyState';
import { useAuth } from 'src/shared/hooks/use-auth';

export const PageErrorBoundary = ({
  children,
  errorPage = 'standard',
  wrapperClass = 'mt-10',
}: {
  children: JSX.Element;
  errorPage?: string;
  wrapperClass?: string;
}) => {
  const userDetails = useAuth((s) => s.userDetails);
  const router = useRouter();
  const handleError = async (error: Error, componentStack: string) => {
    if (process.env.NODE_ENV === 'production') {
      await axios.post('/api/error-report', {
        email: userDetails?.email,
        userId: userDetails?.userId,
        firstName: userDetails?.name,
        errorPage: window.location.href,
        device: detect(),
        errorMessage: error.message || 'No message available!',
        stackTrace: componentStack,
      });
    }
  };

  const errorContent = {
    templateEmbed: [['Uh oh, something went wrong.', ' Please try again after a bit']],
    versionHistory: [
      [
        'Uh oh, this version doesn’t seem to work',
        'Please try another version. Our tech team is working on this',
      ],
    ],
    chat: [['Uh oh, something went wrong.', 'Our tech team is working on this']],
    standard: [['Uh oh, something went wrong.', ' Please refresh your page']],
  };

  const ErrorFallback = () => {
    return (
      <div className={wrapperClass}>
        <EmptyStateDashboard
          contents={errorContent[errorPage]}
          src={EMPTY_STATE_IMAGES.lessonInProgress}
        />
        <div className="mx-auto w-full text-center">
          <Button onClick={() => router.reload()}>Reload</Button>
        </div>
      </div>
    );
  };

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} onError={handleError}>
      {children}
    </Sentry.ErrorBoundary>
  );
};
